// FORGED Ed Systems — DEPLOY005 — v0.5.0 — IntakeStep4Cohort.tsx
// Last touched: 2026-05-20
//
// Step 4 of intake: Your cohort — roster + experience mix + target code,
// process, and position.
//
// DEPLOY005 — split the old single "Target certification" dropdown
// (AWS D1.1 SMAW 2F etc.) into three independent selects: code, process,
// position. Programs targeting a code with process/position to be set by
// the test administrator can leave the latter two as "TBD".

import type {
  CohortBlock,
  TargetCode,
  TargetProcess,
  TargetPosition,
} from "./intakeTypes";
import {
  TARGET_CODE_LABELS,
  TARGET_PROCESS_LABELS,
  TARGET_POSITION_LABELS,
} from "./intakeTypes";

type Props = {
  value: CohortBlock;
  onChange: (block: CohortBlock) => void;
};

// Ordered lists for the dropdowns — controls display order.
const CODE_ORDER: TargetCode[] = [
  "aws_d1_1",
  "aws_d1_2",
  "aws_d1_3",
  "aws_d1_5",
  "asme_ix",
  "api_1104",
  "aws_sense_entry",
  "aws_sense_advanced",
  "aws_sense_expert",
  "nccer_l1",
  "nccer_l2",
  "nccer_l3",
  "awp",
  "tx_hs_welding_series",
  "other",
];

const PROCESS_ORDER: TargetProcess[] = [
  "tbd",
  "smaw",
  "gmaw",
  "gtaw",
  "fcaw",
  "saw",
  "multiple",
  "n_a",
];

const POSITION_ORDER: TargetPosition[] = [
  "tbd",
  "1f", "2f", "3f", "4f",
  "1g", "2g", "3g", "4g", "5g", "6g",
  "multiple",
  "n_a",
];

export default function IntakeStep4Cohort({ value, onChange }: Props) {
  function updateExp(level: "beginner" | "intermediate" | "advanced", count: number) {
    onChange({
      ...value,
      experience_levels: {
        ...value.experience_levels,
        [level]: count,
      },
    });
  }

  const expSum =
    value.experience_levels.beginner +
    value.experience_levels.intermediate +
    value.experience_levels.advanced;
  const expMatches = expSum === value.roster_size;

  return (
    <div>
      <h2>Tell us about your cohort</h2>
      <p className="step-description">
        We work at the cohort level — group numbers, not individual students.
        We never ask for student names or per-student data.
      </p>

      <label className="field">
        <span>How many students are in this cohort?</span>
        <input
          type="number"
          min={1}
          max={500}
          value={value.roster_size}
          onChange={(e) => onChange({ ...value, roster_size: Number(e.target.value) })}
        />
      </label>

      <div className="field">
        <span>Experience mix</span>
        <div className="experience-grid">
          <label className="field-inline">
            <span>Beginner (no prior welding)</span>
            <input
              type="number"
              min={0}
              max={500}
              value={value.experience_levels.beginner}
              onChange={(e) => updateExp("beginner", Number(e.target.value))}
            />
          </label>
          <label className="field-inline">
            <span>Intermediate (some prior, &lt; 100 hrs)</span>
            <input
              type="number"
              min={0}
              max={500}
              value={value.experience_levels.intermediate}
              onChange={(e) => updateExp("intermediate", Number(e.target.value))}
            />
          </label>
          <label className="field-inline">
            <span>Advanced (100+ prior hours)</span>
            <input
              type="number"
              min={0}
              max={500}
              value={value.experience_levels.advanced}
              onChange={(e) => updateExp("advanced", Number(e.target.value))}
            />
          </label>
        </div>
        <div className={"sum-line " + (expMatches ? "sum-line-ok" : "sum-line-warn")}>
          Total: {expSum} {expMatches ? "✓" : "(must equal " + value.roster_size + ")"}
        </div>
      </div>

      <label className="field">
        <span>Target code / program / standard</span>
        <select
          value={value.target_code}
          onChange={(e) => onChange({ ...value, target_code: e.target.value as TargetCode })}
        >
          {CODE_ORDER.map((c) => (
            <option key={c} value={c}>{TARGET_CODE_LABELS[c]}</option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>Welding process (method)</span>
        <select
          value={value.target_process}
          onChange={(e) => onChange({ ...value, target_process: e.target.value as TargetProcess })}
        >
          {PROCESS_ORDER.map((p) => (
            <option key={p} value={p}>{TARGET_PROCESS_LABELS[p]}</option>
          ))}
        </select>
        <small style={{ color: "var(--color-text-muted)" }}>
          Pick "TBD" if the test administrator selects process at test time.
        </small>
      </label>

      <label className="field">
        <span>Welding position</span>
        <select
          value={value.target_position}
          onChange={(e) => onChange({ ...value, target_position: e.target.value as TargetPosition })}
        >
          {POSITION_ORDER.map((p) => (
            <option key={p} value={p}>{TARGET_POSITION_LABELS[p]}</option>
          ))}
        </select>
        <small style={{ color: "var(--color-text-muted)" }}>
          Pick "TBD" if the test administrator selects position at test time.
        </small>
      </label>

      <label className="field">
        <span>Target date</span>
        <input
          type="date"
          value={value.target_date}
          onChange={(e) => onChange({ ...value, target_date: e.target.value })}
        />
      </label>
    </div>
  );
}
